import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, HostBinding, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatToolbar } from '@angular/material/toolbar';
import { StatusIncidentDialogComponent, StatusIncidentDialogData } from '@portal-core/status-incidents/components/status-incident-dialog/status-incident-dialog.component';
import { StatusIncidentStatus } from '@portal-core/status-incidents/enums/status-incident-status.enum';
import { StatusIncidentType } from '@portal-core/status-incidents/enums/status-incident-type.enum';
import { StatusIncident } from '@portal-core/status-incidents/models/status-incident.model';
import { incidentStartAt, isTerminalStatus } from '@portal-core/status-incidents/util/status-incident.util';
import { StatusIncidentBarStateService } from '@portal-core/status-incidents/services/status-incident-bar-state.service';
import { ActiveStatusIncidentProvider } from '@portal-core/status-incidents/services/active-status-incident-provider';
import { StatusIncidentsService } from '@portal-core/status-incidents/services/status-incidents.service';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { Subscription, take } from 'rxjs';

@Component({
  selector: 'mc-status-incident-bar',
  templateUrl: './status-incident-bar.component.html',
  styleUrls: ['./status-incident-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [MatToolbar, MatIcon, MatButton, MatIconButton],
  providers: [DatePipe]
})
@AutoUnsubscribe()
export class StatusIncidentBarComponent implements OnInit {
  private readonly datePipe = inject(DatePipe);
  private readonly dialog = inject(MatDialog);
  private readonly activeStatusIncidentProvider = inject(ActiveStatusIncidentProvider);
  private readonly statusIncidentBarStateService = inject(StatusIncidentBarStateService);
  private readonly statusIncidentsService = inject(StatusIncidentsService);
  private readonly changeDetector = inject(ChangeDetectorRef);

  @HostBinding('class.mc-hidden') _hidden: boolean = true;

  activeIncidentSubscription: Subscription;
  dismissed: boolean = false;
  message: string;

  private incident: StatusIncident;

  ngOnInit() {
    this.activeIncidentSubscription = this.activeStatusIncidentProvider.activeStatusIncident$.subscribe(incident => {
      this.incident = incident;
      this.dismissed = false;
      this._hidden = !incident;
      this.statusIncidentBarStateService.setVisible(!this._hidden);
      if (incident) {
        this.message = this.buildMessage();
      }
      this.changeDetector.markForCheck();
    });
  }

  onMoreInfoClicked() {
    this.statusIncidentsService.getActiveStatusIncident$().pipe(take(1)).subscribe(incident => {
      if (!incident) {
        this.incident = null;
        this.dismissed = false;
        this._hidden = true;
        this.statusIncidentBarStateService.setVisible(false);
        this.changeDetector.markForCheck();
        return;
      }
      const dialogRef = this.dialog.open<StatusIncidentDialogComponent, StatusIncidentDialogData>(
        StatusIncidentDialogComponent, {
        ...StatusIncidentDialogComponent.DialogConfig,
        data: { incident }
      }
      );
      dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
        this.activeStatusIncidentProvider.onRefresh();
      });
    });
  }

  onDismissClicked() {
    this.dismissed = true;
    this._hidden = true;
    this.statusIncidentBarStateService.setVisible(false);
  }

  private buildMessage(): string {
    const date = this.datePipe.transform(incidentStartAt(this.incident), 'MMMM d, y \'at\' h:mm a');

    let lead: string;
    if (isTerminalStatus(this.incident.Status)) {
      lead = 'Resolved';
    } else if (this.incident.Type === StatusIncidentType.Unplanned) {
      lead = `Incident in progress since ${date}`;
    } else if (this.incident.Status === StatusIncidentStatus.Scheduled) {
      lead = `Scheduled maintenance on ${date}`;
    } else {
      lead = `Scheduled maintenance in progress since ${date}`;
    }

    return this.incident.Title ? `${lead}: ${this.incident.Title}` : `${lead}.`;
  }
}
